import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  BadgeCheck,
  Clock,
  MapPin,
  Megaphone,
  Radio,
  RefreshCw,
  Send,
  Sparkles,
  Timer,
  Truck,
  UserCheck,
  Users,
  Zap,
} from 'lucide-react';
import { PageHead, PriorityFlag, JobStatusBadge } from '../components/bits.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  Checkbox,
  Drawer,
  EmptyState,
  Field,
  Input,
  Select,
  Stat,
  Textarea,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { usePoll } from '../lib/hooks.js';
import { compactMoney, dateLabel, hours, money, num, relative, timeLabel, untilLabel } from '../lib/format.js';
import { FREIGHT_PLAIN, distancePlain, durationPlain, matchWords } from '../lib/plain.js';

const COLUMNS = [
  { key: 'unfilled', label: 'Needs someone', tone: 'red', statuses: ['posted', 'draft'] },
  { key: 'assigned', label: 'Someone is coming', tone: 'blue', statuses: ['assigned'] },
  { key: 'rolling', label: 'On the way', tone: 'violet', statuses: ['en_route'] },
  { key: 'onsite', label: 'At the dock', tone: 'lime', statuses: ['checked_in', 'in_progress'] },
];

const VIEWS = [
  { key: 'all', label: 'Everything' },
  { key: 'unfilled', label: 'Nobody booked' },
  { key: 'risk', label: 'Starting soon' },
  { key: 'hot', label: 'Rush jobs' },
  { key: 'today', label: 'Today' },
];

export default function Dispatch() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, loading, refresh } = usePoll('/dispatch/board', { params: { limit: 60 }, ms: 30_000 });
  const [view, setView] = useState('all');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const jobs = data?.jobs || [];
  const kpis = data?.kpis || {};

  const filtered = useMemo(() => {
    const todayKey = new Date().toDateString();
    return jobs.filter((job) => {
      if (view === 'unfilled') return !job.lumperId;
      if (view === 'risk') return job.atRisk;
      if (view === 'hot') return job.priority === 'hot';
      if (view === 'today') return new Date(job.windowStart).toDateString() === todayKey;
      return true;
    });
  }, [jobs, view]);

  const grouped = useMemo(
    () =>
      COLUMNS.map((column) => ({
        ...column,
        jobs: filtered.filter((job) => column.statuses.includes(job.status)),
      })),
    [filtered],
  );

  const autoAssign = async (job) => {
    setBusy(true);
    try {
      const payload = await api.post(`/dispatch/auto-assign/${job.id}`);
      toast.ok(`${payload.data.lumper.name} is doing this one`, `They are ${distancePlain(payload.data.lumper.distanceMiles)}`);
      refresh();
    } catch (err) {
      toast.err('Could not find anyone', err.message);
    } finally {
      setBusy(false);
    }
  };

  const blast = async (job, top = 8) => {
    setBusy(true);
    try {
      const payload = await api.post(`/dispatch/blast/${job.id}`, { top });
      toast.ok(`Asked ${payload.data.offered} ${payload.data.offered === 1 ? 'person' : 'people'}`, 'They get a message on their phone straight away');
      refresh();
    } catch (err) {
      toast.err('Could not send the messages', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="Jobs needing someone"
        subtitle="Every job that is not finished yet, with the ones starting soonest at the top."
        icon={Radio}
        actions={
          <>
            <Button variant="outline" size="sm" icon={RefreshCw} onClick={refresh}>Check again</Button>
            <Button variant="primary" size="sm" icon={Zap} onClick={() => navigate('/app/jobs?new=1')}>
              Add a job
            </Button>
          </>
        }
      />

      <div className="grid grid-4 mb-20">
        <Stat label="Nobody booked yet" icon={AlertTriangle} value={kpis.unfilled || 0} tone={(kpis.unfilled || 0) > 4 ? 'red' : 'amber'} foot="these need a person" />
        <Stat label="Starting in the next 8 hours" icon={Timer} value={kpis.atRisk || 0} tone="red" foot="do these first" />
        <Stat label="On the way" icon={Truck} value={kpis.enRoute || 0} tone="violet" foot="lumpers driving over" />
        <Stat label="Working right now" icon={Users} value={kpis.activeNow || 0} tone="lime" foot="at the dock or unloading" />
      </div>

      <div className="filter-bar">
        <span className="label">Show me</span>
        {VIEWS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={cx('saved-view', view === item.key && 'active')}
            onClick={() => setView(item.key)}
          >
            {item.label}
          </button>
        ))}
        <div className="grow" />
        <span className="dim" style={{ fontSize: 'var(--fs-sm)' }}>
          Showing {filtered.length} of {jobs.length} jobs — updates by itself every 30 seconds
        </span>
      </div>

      {loading && !jobs.length ? (
        <div className="board">
          {COLUMNS.map((column) => (
            <div className="board-col" key={column.key}>
              <div className="board-col-head"><span className="label">{column.label}</span></div>
              {[0, 1, 2].map((i) => <div key={i} className="skeleton board-skeleton" />)}
            </div>
          ))}
        </div>
      ) : (
        <div className="board">
          {grouped.map((column) => (
            <div className="board-col" key={column.key}>
              <div className="board-col-head" data-tone={column.tone}>
                <span className="tone-dot" style={{ '--tone': `var(--${column.tone})` }} />
                <span className="label" style={{ color: 'var(--fg-2)' }}>{column.label}</span>
                <span className="count">{column.jobs.length}</span>
              </div>
              <div className="board-col-body" style={{ gap: 10 }}>
                {column.jobs.map((job) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    onOpen={() => navigate(`/app/jobs/${job.id}`)}
                    onMatch={() => setSelected(job)}
                    onBlast={() => blast(job, 8)}
                    onAuto={() => autoAssign(job)}
                    busy={busy}
                  />
                ))}
                {!column.jobs.length ? (
                  <div className="dim center" style={{ padding: '18px 8px', fontSize: 'var(--fs-sm)' }}>
                    Nothing here
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}

      <MatchDrawer
        job={selected}
        onClose={() => setSelected(null)}
        onDone={() => {
          setSelected(null);
          refresh();
        }}
      />
    </>
  );
}

function JobCard({ job, onOpen, onMatch, onBlast, onAuto, busy }) {
  const risk = job.atRisk;
  return (
    <div className={cx('job-card', risk && 'risk', job.priority === 'hot' && 'hot')}>
      <div className="job-card-head">
        <button
          type="button"
          onClick={onOpen}
          title="Open this job"
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'var(--fg-3)', fontSize: 'var(--fs-xs)', fontWeight: 600 }}
        >
          Job {job.code.replace(/^JOB-/, '')}
        </button>
        <PriorityFlag priority={job.priority} />
        <span className="grow" />
        {job.pendingOffers ? <Badge tone="violet" size="sm">Asked {job.pendingOffers} {job.pendingOffers === 1 ? 'person' : 'people'}</Badge> : null}
      </div>

      <div className="job-card-site truncate">{job.siteName}</div>
      <div className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>
        {job.customerName} · {job.siteCity}, {job.siteState}
      </div>

      <div className="job-card-meta">
        <span className="row gap-4"><Clock size={11} /> {timeLabel(job.windowStart)}</span>
        <span className="row gap-4"><MapPin size={11} /> {dateLabel(job.windowStart)}</span>
        <span className="row gap-4"><Truck size={11} /> {job.palletsExpected} pallets</span>
        {job.dockDoor ? <span>Door {job.dockDoor}</span> : null}
      </div>

      <div className="job-card-money">
        <span className="pay">{money(job.lumperPay)}</span>
        <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
          {job.payType === 'per_pallet' ? 'per pallet' : `${durationPlain(job.estimatedHours)} of work`}
        </span>
        <span className="grow" />
        {risk ? <Badge tone="red" size="sm" dot>{untilLabel(job.windowStart)}</Badge> : <Badge tone="muted" size="sm">{untilLabel(job.windowStart)}</Badge>}
      </div>

      {!job.lumperId ? (
        <>
          {job.candidates?.length ? (
            <div className="col gap-6 mt-12">
              {job.candidates.slice(0, 2).map((candidate) => (
                <div key={candidate.id} className="row between gap-8" style={{ fontSize: 'var(--fs-xs)' }}>
                  <span className="row gap-6 truncate">
                    <Avatar name={candidate.name} tone={candidate.photoColor || 'amber'} size="sm" />
                    <span className="truncate">{candidate.name}</span>
                  </span>
                  <span className="row gap-6">
                    <span className="dim">{candidate.distanceMiles ? `${Math.round(candidate.distanceMiles)} mi` : '—'}</span>
                    <Badge tone={matchWords(candidate.score).tone} size="sm">
                      {matchWords(candidate.score).label}
                    </Badge>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="dim mt-8" style={{ fontSize: 'var(--fs-xs)' }}>
              Nobody close by is free at the moment.
            </div>
          )}

          <div className="row gap-6 mt-12">
            <Button size="sm" variant="primary" icon={UserCheck} onClick={onMatch} className="grow">Give it to someone</Button>
            <Button size="sm" variant="outline" icon={Send} onClick={onBlast} disabled={busy} title="Ask the 8 closest people" />
            <Button size="sm" variant="outline" icon={Sparkles} onClick={onAuto} disabled={busy} title="Let us pick the best person" />
          </div>
        </>
      ) : (
        <div className="row between gap-8 mt-12">
          <Link to={`/app/jobs/${job.id}`} className="row gap-8 truncate">
            <Avatar name={job.lumperName} tone={job.lumperPhotoColor || 'blue'} size="sm" />
            <span className="truncate" style={{ fontSize: 'var(--fs-sm)' }}>{job.lumperName}</span>
          </Link>
          <JobStatusBadge status={job.status} size="sm" />
        </div>
      )}
    </div>
  );
}

function MatchDrawer({ job, onClose, onDone }) {
  const toast = useToast();
  const [includeBusy, setIncludeBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [payOffer, setPayOffer] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { data: candidates, loading, refresh } = usePoll(`/dispatch/candidates/${job?.id}`, {
    params: { includeUnavailable: includeBusy },
    ms: 120_000,
    skip: !job,
  });

  const list = candidates || [];

  const assign = async (lumperId) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.id}/assign`, {
        lumperId,
        payOffer: payOffer ? Number(payOffer) : job.lumperPay,
        note: note || undefined,
      });
      toast.ok('Job given out', `We let ${job.siteName} know someone is coming`);
      onDone();
    } catch (err) {
      toast.err('Could not give out the job', err.message);
    } finally {
      setBusy(false);
    }
  };

  const blastSelected = async () => {
    setBusy(true);
    try {
      const payload = await api.post(`/jobs/${job.id}/broadcast`, {
        lumperIds: selected,
        payOffer: payOffer ? Number(payOffer) : undefined,
      });
      toast.ok(`Asked ${payload.data.offered} ${payload.data.offered === 1 ? 'person' : 'people'}`, 'They get a message on their phone straight away');
      setSelected([]);
      refresh();
    } catch (err) {
      toast.err('Could not send the messages', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      open={Boolean(job)}
      onClose={onClose}
      size="wide"
      title={job ? 'Who should do this job?' : ''}
      subtitle={job ? `${job.customerName} at ${job.siteName}, ${timeLabel(job.windowStart)}` : ''}
      actions={
        job ? (
          <Link to={`/app/jobs/${job.id}`}>
            <Button size="sm" variant="ghost">Open the job</Button>
          </Link>
        ) : null
      }
      footer={
        <>
          <span className="dim grow" style={{ fontSize: 'var(--fs-sm)' }}>
            {list.filter((c) => c.openJobs === 0).length} free right now · {list.length} close enough to help
          </span>
          <Button variant="outline" onClick={() => refresh()}>Check again</Button>
          <Button variant="primary" icon={Megaphone} disabled={!selected.length || busy} onClick={blastSelected}>
            Ask {selected.length || 'these'} {selected.length === 1 ? 'person' : 'people'}
          </Button>
        </>
      }
    >
      {job ? (
        <div className="drawer-stack">
          {job.atRisk ? (
            <Callout tone="red" title={`Someone needs to be there ${untilLabel(job.windowStart)}`}>
              This job starts at {timeLabel(job.windowStart)} on {dateLabel(job.windowStart)} and nobody is booked yet.
            </Callout>
          ) : null}

          <div className="grid grid-2" style={{ gap: 12 }}>
            <Field label="What we will pay them">
              <Input
                className="mono"
                placeholder={money(job.lumperPay)}
                value={payOffer}
                onChange={(e) => setPayOffer(e.target.value)}
                inputMode="decimal"
              />
            </Field>
            <Field label="What the job is">
              <div className="row gap-8 wrap" style={{ paddingTop: 8 }}>
                <Badge tone="amber">{job.palletsExpected} pallets</Badge>
                <Badge tone="muted">{FREIGHT_PLAIN[job.freightType] || job.freightType}</Badge>
                <Badge tone="muted">{durationPlain(job.estimatedHours)} of work</Badge>
              </div>
            </Field>
          </div>

          <Field label="Anything they should know? (optional)">
            <Textarea
              rows={2}
              placeholder="Anything they need to know before they take it…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>

          <div className="row between gap-12">
            <span className="label">People who could do it — best first</span>
            <Checkbox
              label="Also show people already working"
              checked={includeBusy}
              onChange={() => setIncludeBusy((v) => !v)}
            />
          </div>

          {loading && !list.length ? (
            <div className="col gap-8">
              {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 54 }} />)}
            </div>
          ) : list.length ? (
            <div className="col gap-8">
              {list.map((candidate) => {
                const isSelected = selected.includes(candidate.id);
                return (
                  <div key={candidate.id} className={cx('candidate', isSelected && 'selected')}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() =>
                        setSelected((prev) =>
                          prev.includes(candidate.id) ? prev.filter((id) => id !== candidate.id) : [...prev, candidate.id],
                        )
                      }
                    />
                    <Avatar name={candidate.name} tone={candidate.photoColor || 'amber'} />
                    <div className="grow truncate">
                      <div className="row gap-6">
                        <span className="truncate" style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{candidate.name}</span>
                        {candidate.tier !== 'standard' ? (
                          <Badge tone={candidate.tier === 'crew_lead' ? 'amber' : 'teal'} size="sm">
                            {candidate.tier === 'crew_lead' ? 'Runs a crew' : 'One of our best'}
                          </Badge>
                        ) : null}
                        {candidate.skillMatch ? <Badge tone="lime" size="sm" dot>Has done this before</Badge> : null}
                      </div>
                      <span className="reason truncate">{candidate.reasons.join(' · ')}</span>
                    </div>
                    <div className="right col gap-2" style={{ minWidth: 74 }}>
                      <Badge tone={matchWords(candidate.score).tone} size="sm">
                        {matchWords(candidate.score).label}
                      </Badge>
                    </div>
                    <div className="right col gap-2" style={{ minWidth: 92 }}>
                      <span className="num" style={{ fontSize: 'var(--fs-sm)' }}>
                        {candidate.distanceMiles ? distancePlain(candidate.distanceMiles) : 'Not sure how far'}
                      </span>
                      <span className="dimmer" style={{ fontSize: 10 }}>
                        {Number(candidate.rating || 0).toFixed(1)}★ · has done {candidate.jobsCompleted} jobs
                      </span>
                    </div>
                    <Button size="sm" variant="primary" icon={BadgeCheck} disabled={busy} onClick={() => assign(candidate.id)}>
                      Give it to them
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <EmptyState icon={Users} title="Nobody close enough is free">
              Try asking further away, or include people who are already working.
            </EmptyState>
          )}
        </div>
      ) : null}
    </Drawer>
  );
}
